import { LocalDateTime } from '@js-joda/core';
import { Weaken } from 'utils/reusable-types';
import { i18n } from 'i18n';
import {
  BreakingScheduleDto,
  BreakingScheduleSetupForm,
  BreakingScheduleSetupResultDto,
  BreakingTestDto,
  FileParameter,
  FileResponse,
  MoveBreakingScheduleCommand,
  PagedListOfBreakingScheduleDto,
  PagedListOfQAQCTableRecordDto,
  PourStatus
} from './api';
import {
  PageParams,
  makeCreateMutation,
  makeDeleteMutation,
  makeGetQuery,
  makeListQuery,
  makeUpdateMutation
} from './useApi';
import { POURS_KEY } from './usePours';

export interface BreakingScheduleForm {
  sampleName: string;
  slump: number;
  dateCast: LocalDateTime;
  numberOfCylinders: number;
  vane: number;
  slurryDensity: number
  inspectionInterval: number[]
  slumpPicture: FileParameter;
}

export type BreakingScheduleInputs = Weaken<BreakingScheduleForm>;

export const BREAKING_SCHEDULES_KEY = 'breaking-schedules';
export const BREAKING_SCHEDULE_KEY = 'breaking-schedule';

export const useGetBreakingScheduleQuery = makeGetQuery<BreakingScheduleDto, string>(
  BREAKING_SCHEDULE_KEY,
  (api, breakingScheduleId) => () => api.breakingSchedules.get(breakingScheduleId),
);

export interface ListBreakingScheduleQueryParams extends PageParams {
  breakingScheduleId?: string;
  pourId?: string;
  mineModelId?: string;
  startDateUtc?: string;
  endDateUtc?: string;
}

export const useListBreakingSchedulesQuery = makeListQuery<ListBreakingScheduleQueryParams, PagedListOfBreakingScheduleDto>(
  BREAKING_SCHEDULES_KEY,
  (api, params) => () => api.breakingSchedules.list(
    params.page,
    params.pageSize,
    params.breakingScheduleId,
    params.pourId,
    params.mineModelId,
    params.startDateUtc,
    params.endDateUtc,
  ),
);

export interface BreakingSchedulesByPoursQuery {
  mineModelId: string;
  pourIds: string[];
}

// Which of a set of pours already have QAQC sampling (a breaking schedule) set up. Used by the
// approval-stage panel so it never offers to set up a pour twice.
export const useListBreakingSchedulesByPoursQuery = makeGetQuery<BreakingScheduleDto[], BreakingSchedulesByPoursQuery>(
  BREAKING_SCHEDULES_KEY,
  (api, params) => () => api.breakingSchedules.listByPours(params.mineModelId, params.pourIds),
);

export interface SetupBreakingSchedulesForPoursMutation {
  mineModelId: string;
  form: BreakingScheduleSetupForm;
}

export const useSetupBreakingSchedulesForPoursMutation = makeCreateMutation<BreakingScheduleSetupResultDto[], SetupBreakingSchedulesForPoursMutation>(
  BREAKING_SCHEDULES_KEY,
  (api) => (mutation) => api.breakingSchedules.setupForPours(mutation.mineModelId, mutation.form),
  () => i18n._('QAQC sampling has been set up'),
  [POURS_KEY],
);

export const useListByBreakingScheduleIdQuery = makeListQuery<string, BreakingTestDto[]>(
  BREAKING_SCHEDULES_KEY,
  (api, breakingScheduleId) => () => api.breakingSchedules.listByBreakingScheduleId(breakingScheduleId),
);

export interface BreakingScheduleMutation {
  pourId: string;
  breakingScheduleForm: BreakingScheduleForm;
}

// NOTE: Multipart forms cannot have a body. Nswag unpacks the server form into separate params
export const useCreateBreakingScheduleMutation = makeCreateMutation<string, BreakingScheduleMutation>(
  BREAKING_SCHEDULES_KEY,
  (api) => (breakingScheduleMutation) => api.breakingSchedules.create(
      breakingScheduleMutation.pourId,
      breakingScheduleMutation.breakingScheduleForm.sampleName,
      breakingScheduleMutation.breakingScheduleForm.slump,
      breakingScheduleMutation.breakingScheduleForm.dateCast.toJSON(),
      breakingScheduleMutation.breakingScheduleForm.numberOfCylinders,
      breakingScheduleMutation.breakingScheduleForm.vane,
      breakingScheduleMutation.breakingScheduleForm.slurryDensity,
      breakingScheduleMutation.breakingScheduleForm.inspectionInterval,
      breakingScheduleMutation.breakingScheduleForm.slumpPicture,
    ),
  () => i18n._('The breaking schedule has been saved'),
);

export const useDeleteBreakingScheduleMutation = makeDeleteMutation<void, string >(
  BREAKING_SCHEDULE_KEY,
  BREAKING_SCHEDULES_KEY,
  (api) => (breakingScheduleId) => api.breakingSchedules.delete(breakingScheduleId),
  () => i18n._('Breaking Schedule successfully deleted'),
  (breakingScheduleId) => breakingScheduleId,
);

export const useMoveBreakingScheduleMutation = makeUpdateMutation<FileResponse, MoveBreakingScheduleCommand>(
  BREAKING_SCHEDULE_KEY,
  BREAKING_SCHEDULES_KEY,
  (api, breakingScheduleId) => (command) => api.breakingSchedules.move(breakingScheduleId, command),
  () => i18n._('QAQC data moved successfully'),
  [POURS_KEY, BREAKING_SCHEDULES_KEY],
);

export interface ListQAQCTableRecordsParams extends PageParams {
  mineModelId: string;
  startDateUtc?: string;
  endDateUtc?: string;
  pourReference?: string;
  pourStatus?: PourStatus;
  stopeId?: string;
}

export const useListQAQCTableRecordsQuery = makeListQuery<ListQAQCTableRecordsParams, PagedListOfQAQCTableRecordDto>(
  BREAKING_SCHEDULES_KEY,
  (api, params) => () => api.breakingSchedules.listQAQCTableRecords(
    params.mineModelId,
    params.page,
    params.pageSize,
    params.startDateUtc,
    params.endDateUtc,
    params.pourReference,
    params.pourStatus,
    params.stopeId,
  ),
);
